import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
  useMemo,
} from "react";
import { Loader2, Info, X, CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface Notification {
  id: string;
  message: string;
  type: "loading" | "success" | "error" | "info" | "warning";
  duration?: number;
}

interface NotificationContextType {
  showNotification: (
    message: string,
    type: "loading" | "success" | "error" | "info" | "warning",
    duration?: number
  ) => string;
  hideNotification: (id: string) => void;
  showLoading: (message: string) => string;
  showSuccess: (message: string, duration?: number) => string;
  showError: (message: string, duration?: number) => string;
  showInfo: (message: string, duration?: number) => string;
  showWarning: (message: string, duration?: number) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle auto-hide timer for non-loading notifications
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (
      currentNotification &&
      currentNotification.type !== "loading" &&
      currentNotification.duration &&
      currentNotification.duration > 0
    ) {
      const notificationId = currentNotification.id;
      const notificationDuration = currentNotification.duration;

      timeoutRef.current = setTimeout(() => {
        setCurrentNotification((n) => {
          if (n?.id === notificationId) {
            timeoutRef.current = null;
            return null;
          }
          return n;
        });
      }, notificationDuration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [currentNotification?.id, currentNotification?.type, currentNotification?.duration]);

  const hideNotification = useCallback((id: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setCurrentNotification((current) => {
      if (current?.id === id) {
        return null;
      }
      return current;
    });
  }, []);

  const showNotification = useCallback(
    (
      message: string,
      type: "loading" | "success" | "error" | "info" | "warning",
      duration: number = type === "loading" ? 0 : 3000
    ): string => {
      const id = `notif-${Date.now()}-${Math.random()}`;
      const notification: Notification = { id, message, type, duration };

      setCurrentNotification(notification);
      return id;
    },
    []
  );

  const showLoading = useCallback(
    (message: string) => {
      return showNotification(message, "loading", 0);
    },
    [showNotification]
  );

  const showSuccess = useCallback(
    (message: string, duration: number = 5000) => {
      return showNotification(message, "success", duration);
    },
    [showNotification]
  );

  const showError = useCallback(
    (message: string, duration: number = 7000) => {
      return showNotification(message, "error", duration);
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string, duration: number = 5000) => {
      return showNotification(message, "info", duration);
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string, duration: number = 5000) => {
      return showNotification(message, "warning", duration);
    },
    [showNotification]
  );

  const contextValue = useMemo(
    () => ({
      showNotification,
      hideNotification,
      showLoading,
      showSuccess,
      showError,
      showInfo,
      showWarning,
    }),
    [showNotification, hideNotification, showLoading, showSuccess, showError, showInfo, showWarning]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Floating Non-Blocking Toast Banner Container */}
      <div className="fixed inset-x-3 top-3 z-[9999] pointer-events-none flex flex-col items-center gap-2 sm:inset-x-auto sm:right-4 sm:top-4 sm:w-full sm:max-w-sm sm:items-end">
        <AnimatePresence>
          {currentNotification && (
            <motion.div
              key={currentNotification.id}
              initial={{ opacity: 0, y: -16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.18 }}
              className={cn(
                "pointer-events-auto relative w-full max-w-sm rounded-xl border border-l-4 border-white/50 bg-white/75 p-3.5 shadow-xl shadow-slate-950/10 ring-1 ring-white/30 backdrop-blur-xl transition-all flex items-start gap-3 dark:border-white/15 dark:bg-slate-950/70 dark:shadow-black/25 dark:ring-white/10",
                currentNotification.type === "success" &&
                  "border-border border-l-emerald-600 text-card-foreground",
                currentNotification.type === "error" &&
                  "border-border border-l-red-600 text-card-foreground",
                currentNotification.type === "warning" &&
                  "border-border border-l-amber-600 text-card-foreground",
                currentNotification.type === "info" &&
                  "border-border border-l-blue-600 text-card-foreground",
                currentNotification.type === "loading" &&
                  "border-border border-l-indigo-600 text-card-foreground"
              )}
              role={currentNotification.type === "error" ? "alert" : "status"}
              aria-live={currentNotification.type === "error" ? "assertive" : "polite"}
              aria-atomic="true"
            >
              {/* Icon */}
              <div className="flex-none pt-0.5">
                {currentNotification.type === "success" && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                )}
                {currentNotification.type === "error" && (
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                )}
                {currentNotification.type === "warning" && (
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                )}
                {currentNotification.type === "info" && (
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
                {currentNotification.type === "loading" && (
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-600 dark:text-indigo-400" />
                )}
              </div>

              {/* Message */}
              <div className="flex-1 min-w-0 pr-4">
                <div className="text-xs font-semibold text-foreground">
                  {currentNotification.type === "success"
                    ? "Success"
                    : currentNotification.type === "error"
                    ? "Error"
                    : currentNotification.type === "warning"
                    ? "Warning"
                    : currentNotification.type === "loading"
                    ? "Processing"
                    : "Information"}
                </div>
                <div className="text-sm text-foreground/85 mt-0.5 leading-relaxed break-words font-normal">
                  {currentNotification.message}
                </div>
              </div>

              {/* Close Button */}
              {currentNotification.type !== "loading" && (
                <button
                  type="button"
                  onClick={() => hideNotification(currentNotification.id)}
                  className="flex-none p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  aria-label="Close notification"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};
